// services/queryService.js
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs, 
  startAfter,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from './firebaseConfig';

/**
 * Get all standard queries with optional pagination
 * @param {Object} options - Query options
 * @param {number} options.limitCount - Number of queries to fetch (default: 100)
 * @param {DocumentSnapshot} options.lastDoc - Last document for pagination
 * @returns {Promise} Result object with queries array and success status
 */
export const getStandardQueries = async (options = {}) => {
  try {
    const { limitCount = 100, lastDoc = null } = options;
    
    let q = query(
      collection(db, 'standardQueries'),
      where('isActive', '==', true),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    // Add pagination if lastDoc is provided
    if (lastDoc) {
      q = query(
        collection(db, 'standardQueries'),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc'),
        startAfter(lastDoc),
        limit(limitCount)
      );
    }

    const snapshot = await getDocs(q);
    
    const queries = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      // Ensure we have the timestamp as a JavaScript Date
      createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt,
      updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : doc.data().updatedAt
    }));

    return {
      success: true,
      queries,
      lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
      hasMore: snapshot.docs.length === limitCount
    };
  } catch (error) {
    console.error('Error fetching standard queries:', error);
    return {
      success: false,
      error: error.message,
      queries: []
    };
  }
};

/**
 * Get queries filtered by specific criteria
 * @param {Object} filters - Filter criteria
 * @param {string} filters.sheetSource - Sheet source filter
 * @param {string} filters.queryType - Query type filter
 * @param {string} filters.requirement - Requirement filter
 * @param {string} filters.searchTerm - Search term for text search
 * @param {number} filters.limitCount - Number of queries to fetch
 * @returns {Promise} Result object with filtered queries
 */
export const getQueriesByFilter = async (filters = {}) => {
  try {
    const {
      sheetSource = 'all',
      queryType = 'all',
      requirement = 'all',
      searchTerm = '',
      limitCount = 100
    } = filters;

    // Start with base query
    let constraints = [
      where('isActive', '==', true),
      orderBy('createdAt', 'desc')
    ];

    // Add sheet source filter
    if (sheetSource !== 'all') {
      constraints.push(where('sheetSource', '==', sheetSource));
    }

    // Add query type filter
    if (queryType !== 'all') {
      constraints.push(where('queryType', '==', queryType));
    }

    // Add requirement filter (this is more complex due to array/string nature)
    if (requirement !== 'all') {
      // For array fields, we use array-contains
      // For string fields, we use == operator
      // We'll try both approaches since the data might be inconsistent
      constraints.push(where('requirementStages', 'array-contains', requirement));
    }

    // Add limit
    constraints.push(limit(limitCount));

    const q = query(collection(db, 'standardQueries'), ...constraints);
    const snapshot = await getDocs(q);
    
    let queries = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt,
      updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : doc.data().updatedAt
    }));

    // Apply text search filter (client-side since Firestore doesn't support full-text search)
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      queries = queries.filter(query =>
        query.queryDescription?.toLowerCase().includes(searchLower) ||
        query.queryHeader?.toLowerCase().includes(searchLower) ||
        query.documentDescription?.toLowerCase().includes(searchLower) ||
        query.department?.toLowerCase().includes(searchLower) ||
        query.documentCategory?.toLowerCase().includes(searchLower)
      );
    }

    // Apply additional requirement filter for string-based requirements (fallback)
    if (requirement !== 'all') {
      queries = queries.filter(query =>
        query.requirement?.includes(requirement) ||
        query.requirementStages?.includes(requirement)
      );
    }

    return {
      success: true,
      queries,
      totalCount: queries.length
    };
  } catch (error) {
    console.error('Error fetching filtered queries:', error);
    return {
      success: false,
      error: error.message,
      queries: []
    };
  }
};

/**
 * Get a single query by ID
 * @param {string} queryId - Query document ID
 * @returns {Promise} Result object with query data
 */
export const getQueryById = async (queryId) => {
  try {
    const docRef = doc(db, 'standardQueries', queryId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const queryData = {
        id: docSnap.id,
        ...docSnap.data(),
        createdAt: docSnap.data().createdAt?.toDate ? docSnap.data().createdAt.toDate() : docSnap.data().createdAt,
        updatedAt: docSnap.data().updatedAt?.toDate ? docSnap.data().updatedAt.toDate() : docSnap.data().updatedAt
      };
      
      return {
        success: true,
        query: queryData
      };
    } else {
      return {
        success: false,
        error: 'Query not found'
      };
    }
  } catch (error) {
    console.error('Error fetching query by ID:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Search queries by text (more advanced search)
 * @param {string} searchTerm - Search term
 * @param {Object} options - Search options
 * @param {number} options.limitCount - Number of results to return
 * @param {Array} options.fields - Fields to search in
 * @returns {Promise} Result object with search results
 */
export const searchQueries = async (searchTerm, options = {}) => {
  try {
    const { limitCount = 50, fields = ['queryDescription', 'queryHeader', 'documentDescription'] } = options;
    
    if (!searchTerm.trim()) {
      return {
        success: true,
        queries: [],
        totalCount: 0
      };
    }

    // Get all active queries (we'll filter client-side)
    // Note: For production, consider using Algolia or similar for better search
    const q = query(
      collection(db, 'standardQueries'),
      where('isActive', '==', true),
      orderBy('createdAt', 'desc'),
      limit(500) // Get more results to filter from
    );

    const snapshot = await getDocs(q);
    const allQueries = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt,
      updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : doc.data().updatedAt
    }));

    // Perform client-side search with scoring
    const searchLower = searchTerm.toLowerCase();
    const searchWords = searchLower.split(' ').filter(word => word.length > 1);
    
    const scoredResults = allQueries.map(query => {
      let score = 0;
      let matchedFields = [];

      fields.forEach(field => {
        const fieldValue = query[field]?.toLowerCase() || '';
        
        // Exact phrase match gets highest score
        if (fieldValue.includes(searchLower)) {
          score += 10;
          matchedFields.push(field);
        }
        
        // Individual word matches
        searchWords.forEach(word => {
          if (fieldValue.includes(word)) {
            score += 2;
            if (!matchedFields.includes(field)) {
              matchedFields.push(field);
            }
          }
        });

        // Boost score for matches in important fields
        if (field === 'queryHeader' && fieldValue.includes(searchLower)) {
          score += 5;
        }
      });

      return {
        ...query,
        searchScore: score,
        matchedFields
      };
    })
    .filter(result => result.searchScore > 0)
    .sort((a, b) => b.searchScore - a.searchScore)
    .slice(0, limitCount);

    return {
      success: true,
      queries: scoredResults,
      totalCount: scoredResults.length,
      searchTerm
    };
  } catch (error) {
    console.error('Error searching queries:', error);
    return {
      success: false,
      error: error.message,
      queries: []
    };
  }
};

/**
 * Get unique filter values for dropdowns
 * @returns {Promise} Result object with filter options
 */
export const getFilterOptions = async () => {
  try {
    // Get a sample of queries to extract unique values
    const q = query(
      collection(db, 'standardQueries'),
      where('isActive', '==', true),
      limit(1000) // Adjust based on your data size
    );

    const snapshot = await getDocs(q);
    const queries = snapshot.docs.map(doc => doc.data());

    // Extract unique values
    const sheetSources = [...new Set(queries.map(q => q.sheetSource).filter(Boolean))];
    const queryTypes = [...new Set(queries.map(q => q.queryType).filter(Boolean))];
    const departments = [...new Set(queries.map(q => q.department).filter(Boolean))];
    const documentCategories = [...new Set(queries.map(q => q.documentCategory).filter(Boolean))];
    
    // Extract requirements from both string and array fields
    const requirements = new Set();
    queries.forEach(query => {
      if (query.requirement) {
        // Handle string requirements
        if (typeof query.requirement === 'string') {
          query.requirement.split('/').forEach(req => requirements.add(req.trim()));
        }
      }
      if (query.requirementStages && Array.isArray(query.requirementStages)) {
        // Handle array requirements
        query.requirementStages.forEach(req => requirements.add(req.trim()));
      }
    });

    return {
      success: true,
      filterOptions: {
        sheetSources: sheetSources.sort(),
        queryTypes: queryTypes.sort(),
        departments: departments.sort(),
        documentCategories: documentCategories.sort(),
        requirements: [...requirements].sort()
      }
    };
  } catch (error) {
    console.error('Error fetching filter options:', error);
    return {
      success: false,
      error: error.message,
      filterOptions: {
        sheetSources: [],
        queryTypes: [],
        departments: [],
        documentCategories: [],
        requirements: []
      }
    };
  }
};

/**
 * Get queries by requirement stage
 * @param {string} stage - Requirement stage (KYP, Discharge, Scrutiny)
 * @param {number} limitCount - Number of queries to fetch
 * @returns {Promise} Result object with queries for the stage
 */
export const getQueriesByStage = async (stage, limitCount = 100) => {
  try {
    // Try array-contains first
    let q = query(
      collection(db, 'standardQueries'),
      where('isActive', '==', true),
      where('requirementStages', 'array-contains', stage),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    let queries = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt,
      updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : doc.data().updatedAt
    }));

    // If no results, try string-based search as fallback
    if (queries.length === 0) {
      const allQ = query(
        collection(db, 'standardQueries'),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc'),
        limit(500)
      );
      
      const allSnapshot = await getDocs(allQ);
      const allQueries = allSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt,
        updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : doc.data().updatedAt
      }));

      queries = allQueries
        .filter(query => query.requirement?.includes(stage))
        .slice(0, limitCount);
    }

    return {
      success: true,
      queries,
      stage,
      totalCount: queries.length
    };
  } catch (error) {
    console.error('Error fetching queries by stage:', error);
    return {
      success: false,
      error: error.message,
      queries: []
    };
  }
};

/**
 * Get recently used queries (this would be based on user interaction tracking)
 * @param {string} userId - User ID
 * @param {number} limitCount - Number of queries to fetch
 * @returns {Promise} Result object with recently used queries
 */
export const getRecentlyUsedQueries = async (userId, limitCount = 10) => {
  try {
    // This would require a separate collection to track query usage
    // For now, we'll return most recently created queries as a placeholder
    const q = query(
      collection(db, 'standardQueries'),
      where('isActive', '==', true),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    const queries = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt,
      updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : doc.data().updatedAt
    }));

    return {
      success: true,
      queries,
      userId
    };
  } catch (error) {
    console.error('Error fetching recently used queries:', error);
    return {
      success: false,
      error: error.message,
      queries: []
    };
  }
};

/**
 * Track query usage (for analytics and recent queries)
 * @param {string} queryId - Query ID that was used
 * @param {string} userId - User ID who used the query
 * @param {string} applicationId - Application ID where it was used
 * @returns {Promise} Result object
 */
export const trackQueryUsage = async (queryId, userId, applicationId) => {
  try {
    // This would add a record to a query usage tracking collection
    // Implementation depends on your analytics requirements
    
    const usageData = {
      queryId,
      userId,
      applicationId,
      usedAt: new Date(),
      timestamp: new Date()
    };

    // You could add this to a 'queryUsage' collection
    // const usageRef = await addDoc(collection(db, 'queryUsage'), usageData);

    return {
      success: true,
      message: 'Query usage tracked'
    };
  } catch (error) {
    console.error('Error tracking query usage:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Batch operations for better performance
 */
export const batchOperations = {
  /**
   * Get multiple queries by IDs
   * @param {Array} queryIds - Array of query IDs
   * @returns {Promise} Result object with queries
   */
  getQueriesByIds: async (queryIds) => {
    try {
      const queries = [];
      
      // Firestore has a limit of 10 documents per batch get
      const batches = [];
      for (let i = 0; i < queryIds.length; i += 10) {
        const batch = queryIds.slice(i, i + 10);
        batches.push(batch);
      }

      for (const batch of batches) {
        const batchPromises = batch.map(id => getDoc(doc(db, 'standardQueries', id)));
        const batchResults = await Promise.all(batchPromises);
        
        batchResults.forEach(docSnap => {
          if (docSnap.exists()) {
            queries.push({
              id: docSnap.id,
              ...docSnap.data(),
              createdAt: docSnap.data().createdAt?.toDate ? docSnap.data().createdAt.toDate() : docSnap.data().createdAt,
              updatedAt: docSnap.data().updatedAt?.toDate ? docSnap.data().updatedAt.toDate() : docSnap.data().updatedAt
            });
          }
        });
      }

      return {
        success: true,
        queries
      };
    } catch (error) {
      console.error('Error in batch get queries:', error);
      return {
        success: false,
        error: error.message,
        queries: []
      };
    }
  }
};

// Export all functions
export default {
  getStandardQueries,
  getQueriesByFilter,
  getQueryById,
  searchQueries,
  getFilterOptions,
  getQueriesByStage,
  getRecentlyUsedQueries,
  trackQueryUsage,
  batchOperations
};